import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChipsInterface } from './SearchBar.types';
import {
  BROWSE_ALL_INITIAL_COUNT,
  buildBrowseButtonLabel,
  ChipItem,
  dedupeChipsByRecents,
  DEFAULT_SEARCHBAR_WIDTH,
  filterChipsByQuery,
  findChipItem,
  getChipLabel,
  useRecentChips,
} from './SearchBar.utils';
import SearchBarField, { SearchBarFieldProps } from './SearchBarField';
import { BrowseAllExpandButton, BrowseAllSection, NoMatchesState, RecentChipsSection } from './SearchBarSections';

export interface SearchBarChipsProps extends SearchBarFieldProps {
  expanded?: boolean;
  chips?: ChipsInterface;
  onSetExpanded?: (value: boolean) => void;
  recentsStorageKey?: string;
  enhancedDropdown?: boolean;
}

const chipFieldExpandedStyle = {
  '& .MuiInputBase-root.MuiOutlinedInput-root fieldset': {
    border: 'none',
    borderRadius: 0,
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
};

const useBrowseAllToggle = (resetTriggers: { trimmedInput: string; isOpen: boolean }) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (resetTriggers.trimmedInput.length > 0) setExpanded(false);
  }, [resetTriggers.trimmedInput]);

  useEffect(() => {
    if (!resetTriggers.isOpen) setExpanded(false);
  }, [resetTriggers.isOpen]);

  return { expanded, expand: () => setExpanded(true) };
};

const SearchBarChips = ({
  chips,
  expanded = false,
  onChange = () => {},
  onSetExpanded,
  recentsStorageKey,
  enhancedDropdown = false,
  ...rest
}: SearchBarChipsProps) => {
  const [selectedChip, setSelectedChip] = useState('');
  const [expandSection, setExpandSection] = useState(expanded);

  useEffect(() => {
    setExpandSection(expanded);
  }, [expanded]);

  const { recents, pushRecent } = useRecentChips(enhancedDropdown ? recentsStorageKey : undefined);

  const inputValue = rest.value ?? '';
  const trimmedInput = inputValue.trim().toLowerCase();
  const { expanded: browseAllExpanded, expand: expandBrowseAll } = useBrowseAllToggle({
    trimmedInput,
    isOpen: expandSection,
  });

  const handleChipSelected = useCallback(
    (value: string) => {
      setSelectedChip(value);
      if (enhancedDropdown && chips?.data) {
        const match = findChipItem(chips.data, value);
        pushRecent({ label: match ? getChipLabel(match) : value, value });
      }
      chips?.setSearch && onChange(value);
      setExpandSection(false);
      onSetExpanded && onSetExpanded(false);
      chips?.onClick && chips.onClick(value);
    },
    [onChange, onSetExpanded, chips, pushRecent, enhancedDropdown],
  );

  const filteredData = useMemo<ChipItem[] | undefined>(() => {
    if (!chips?.data) return chips?.data;
    if (!enhancedDropdown) return chips.data;
    return filterChipsByQuery<ChipItem>(chips.data, trimmedInput);
  }, [chips?.data, trimmedInput, enhancedDropdown]);

  const browseAllData = useMemo<ChipItem[] | undefined>(() => {
    if (!filteredData) return filteredData;
    if (!enhancedDropdown || trimmedInput.length > 0) return filteredData;
    return dedupeChipsByRecents(filteredData, recents);
  }, [filteredData, recents, trimmedInput, enhancedDropdown]);

  const hasDropdownContent = !!chips?.data && chips.data.length > 0;
  const hasFilteredMatches = !!filteredData && filteredData.length > 0;
  const hasBrowseAllItems = !!browseAllData && browseAllData.length > 0;
  const showNoMatchesState = enhancedDropdown && hasDropdownContent && !hasFilteredMatches && trimmedInput.length > 0;

  const isCollapsedBrowse =
    enhancedDropdown && !browseAllExpanded && hasBrowseAllItems && browseAllData!.length > BROWSE_ALL_INITIAL_COUNT;
  const displayedChips = isCollapsedBrowse ? browseAllData!.slice(0, BROWSE_ALL_INITIAL_COUNT) : (browseAllData ?? []);
  const browseButtonLabel = buildBrowseButtonLabel(browseAllData?.length ?? 0, inputValue.trim());

  const showRecentSection = enhancedDropdown && trimmedInput.length === 0 && recents.length > 0;
  const showBrowseAllHeading = enhancedDropdown && trimmedInput.length === 0 && hasBrowseAllItems;

  return (
    <Box role="searchbarchips" sx={{ width: '100%', position: 'relative' }}>
      <SearchBarField
        onChange={onChange}
        onFocus={() => {
          if (chips?.data && chips.data.length > 0) {
            setExpandSection(true);
            onSetExpanded && onSetExpanded(true);
          }
        }}
        sxProps={expanded ? chipFieldExpandedStyle : {}}
        {...rest}
      />
      {expandSection && hasDropdownContent && (
        <Box
          role="chipsbox"
          sx={(theme) => ({
            backgroundColor: theme.palette.common.white,
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            minWidth: DEFAULT_SEARCHBAR_WIDTH,
            maxHeight: 'min(60vh, 480px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderRadius: 1,
            zIndex: 10,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            boxShadow: theme.shadows[3],
          })}
        >
          {showRecentSection && (
            <RecentChipsSection recents={recents} selectedValue={selectedChip} onSelect={handleChipSelected} />
          )}
          {hasBrowseAllItems && (
            <BrowseAllSection
              showHeading={showBrowseAllHeading}
              data={displayedChips}
              sourceData={chips!.data}
              selectedValue={selectedChip}
              onSelect={handleChipSelected}
            />
          )}
          {isCollapsedBrowse && <BrowseAllExpandButton label={browseButtonLabel} onClick={expandBrowseAll} />}
          {showNoMatchesState && <NoMatchesState query={inputValue} />}
        </Box>
      )}
    </Box>
  );
};

export default SearchBarChips;
